// src/services/analytics.js
import { initializeApp } from 'firebase/app'
import { initializeAnalytics, setUserProperties, setUserId, logEvent } from 'firebase/analytics'
import { log } from '@/utils/logger'
import '@/utils/kettle-polyfill'

const firebaseConfig = {
	apiKey: 'AIzaSyDO5ZCq4iCcEM8fcjd1uWgPWwHcNcWcmhU',
	authDomain: 'rvu-reisevaner.firebaseapp.com',
	projectId: 'rvu-reisevaner',
	storageBucket: 'rvu-reisevaner.firebasestorage.app',
	messagingSenderId: '526870634660',
	appId: '1:526870634660:web:18d9332a6ebcbe11eb0631',
	measurementId: 'G-6QHL7QQ27M',
}

// Initialize Firebase
const app = initializeApp(firebaseConfig)

if (typeof window.kettle === 'undefined') {
	console.warn('Analytics: Kettle is not available')
}

// Initialize Analytics
let analytics = null
if (typeof window !== 'undefined') {
	// Extract initialization so we avoid top-level await.
	const setupAnalytics = (debugMode = '0') => {
		// Check if we are in a debug context (e.g., localhost or a specific URL param)
		if (location.hostname === 'localhost' || location.hostname == 'kogo.zrok.kogenta.com' || location.href.includes('debug') || debugMode == '1') {
			log('Firebase Analytics Debug Mode ENABLED').yellow()
			// Initialize with debug_mode set to true
			analytics = initializeAnalytics(app, {
				config: {
					debug_mode: true,
				},
			})
		} else {
			// For all other cases (production), initialize normally
			analytics = initializeAnalytics(app)
		}

		// After analytics is initialized, set a friendly user ID if present in URL
		try {
			if (location.href.includes('applicationId')) {
				var userId = new URLSearchParams(location.search).get('applicationId')
				setUserId(analytics, userId)
				log(`Analytics: setUserId`, userId).blue()
			} else if (location.href.includes('id')) {
				var userId = new URLSearchParams(location.search).get('id')
				setUserId(analytics, userId)
				log(`Analytics: setUserId`, userId).blue()
			}
		} catch (e) {
			// ignore location errors during SSR or unusual environments
		}
	}

	setupAnalytics('0')
	// Load appSetting for debugMode from kettle SDK (async). If kettle is not available, initialize immediately.
	// if (typeof kettle != 'undefined') {
	// 	kettle
	// 		.get('debugMode')
	// 		.then((dm) => setupAnalytics(dm || '0'))
	// 		.catch(() => setupAnalytics('0'))
	// } else {
	// 	setupAnalytics('0')
	// }
}

export const setAnalyticsUserProperties = (appStore = {}, dataStore = {}) => {
	const userProperties = {
		app_version: appStore.version,
		app_installation_id: dataStore.appInstallationId,
		respondent_short_id: dataStore.respondentShortId,
		respondent_alt_id: dataStore.respondentAltId,
		respondent_id: dataStore.respondentId,
		install_date: dataStore.installDate.toISOString(),
		onboarded_permissions: appStore.onboardedPermissions,
		onboarded_survey: appStore.onboardedSurvey,
		etl_version: Etl?.DEFAULTS?.etlVersion || '',
	}

	Object.keys(userProperties).forEach((k) => (userProperties[k] === undefined || userProperties[k] == '' || userProperties[k] === null) && delete userProperties[k])

	setUserProperties(analytics, userProperties)
	log(`Analytics: UserProperties`, userProperties).blue()
}

// Analytics helper function
export const trackEvent = (eventName, parameters = {}) => {
	if (analytics) {
		logEvent(analytics, eventName, parameters)
		log(`Analytics: ${eventName}`, parameters).blue()
	}
}

// Error/exception tracking helper
// Sends a GA4 'exception' event with standard params and some useful context
export const trackException = (errorOrMessage, details = {}, appStore = {}, dataStore = {}) => {
	try {
		const err = errorOrMessage instanceof Error ? errorOrMessage : new Error(String(errorOrMessage))

		// Trim fields to stay safe for GA4 param limits
		const truncate = (v, n = 1000) => (typeof v === 'string' ? v.slice(0, n) : v)

		const params = {
			// GA4 recommended params for exceptions
			description: truncate(details.description || err.message || 'Unknown error', 200),
			fatal: Boolean(details.fatal),

			// Extra helpful context
			name: truncate(err.name || 'Error', 100),
			stack: truncate(err.stack || ''),
			url: truncate(details.url || (typeof location !== 'undefined' ? location.href : '')),
			user_agent: truncate(details.user_agent || (typeof navigator !== 'undefined' ? navigator.userAgent : '')),
			lineno: details.lineno ?? undefined,
			colno: details.colno ?? undefined,
			type: truncate(details.type || 'exception'),
			component: truncate(details.component),
			vue_info: truncate(details.vue_info || '', 200),
			source: truncate(details.source || ''),

			// User info
			respondent_altid: dataStore.respondentAltId || '',
			respondent_shortid: dataStore.respondentShortId || '',

			// App Info
			app_installation_id: dataStore.appInstallationId || '',
			app_id: appStore.applicationId || 'no.opinion.reisevaner',
			app_version: dataStore.appUserAgent?.appVersion || '',
			app_state: dataStore.appState || '',
			platform: dataStore.platform || 'web',

			// States
			current_page: appStore.currentPage || '',
			previous_page: appStore.previousPage || '',
			edit_step: appStore.editStep || '',
			visible_geocoder: dataStore.showGeocoder || '',
			current_location_type: dataStore.currentLocationType || '',
			journey_index: dataStore.currentJourneyIndex || '',
			selected_date: dataStore.selectedDate?.toShortDateString?.() || '',
			onboarded_survey: appStore.onboardedSurvey,
			onboarded_permissions: appStore.onboardedPermissions,
			tracking: appStore.tracking,
			tracking_timeout: appStore.trackingTimeout,
			processing_error: dataStore.processingError,
			permissions: JSON.stringify(appStore.permissions),
			consents: JSON.stringify(appStore.consents),
			object: details.object ? JSON.stringify(details.object || {}) : '',
			info: details.info ? JSON.stringify(details.info) : '',
			places: dataStore.placesRaw || '',

			// Loading states
			load_source: window.loadSource || '',
			load_count: window.loadCount || 0,
			load_step: window.loadStep || 0,
			load_error: window.loadError ? JSON.stringify(window.loadError) : null,
		}

		// Remove undefined to avoid noisy params
		Object.keys(params).forEach((k) => (params[k] === undefined || params[k] == '' || params[k] === null) && delete params[k])

		// Only send exception if host name is rvu.kogenta.com
		if ((typeof location !== 'undefined' && location.hostname === 'rvu.kogenta.com') || location.href.indexOf('debug') > -1) {
			trackEvent('exception', params)

			// Send to Azure Application Insights
			sendApplicationErrorToAzure(params, err, appStore, dataStore)
		} else {
			console.error('trackEvent exception', params, 'page:', appStore.currentPage, JSON.stringify(appStore))
		}
	} catch (e) {
		// Never let analytics throw
		console.warn('trackException failed', e)
	}
}

async function sendApplicationErrorToAzure(params, err, appStore = {}, dataStore = {}) {
	const apiUrl = 'https://norwayeast-0.in.applicationinsights.azure.com/v2/track'
	const apiKey = '72b43a15-5edf-45c2-bb52-dc2f97416b37'

	// Default values can be defined here or sourced from appStore/params
	const appInstallationId = params.app_installation_id
	const appId = params.app_id
	const appVersion = appStore.version || 'unknown'
	const platform = dataStore.platform || 'web'

	const content = {
		name: 'AppEvents',
		time: new Date().toISOString(),
		iKey: apiKey,
		tags: {
			'ai.user.id': appInstallationId,
			'ai.application.id': appId,
			'ai.application.ver': appVersion,
			'ai.application.platform': platform,
			'ai.cloud.roleInstance': params.user_agent || (typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'),
		},
		data: {
			baseType: 'ExceptionData',
			baseData: {
				ver: 2,
				name: params.type || 'exception', // e.g., 'vue_error', 'window_error'
				exceptions: [
					{
						typeName: err.name,
						message: err.message,
						hasFullStack: true,
						stack: err.stack || 'No stack available',
					},
				],
				properties: {
					...params,
					message: params.description, // Ensure primary description is a property
				},
			},
		},
	}

	try {
		const response = await fetch(apiUrl, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(content),
		})

		if (response.status < 200 || response.status >= 400) {
			console.warn(`Failed to send error to Application Insights. Status: ${response.status}`)
		}
	} catch (error) {
		// Log network errors without throwing, to avoid breaking the app
		console.warn('Network error when sending application error to Azure:', error)
	}
}

// Your specific funnel events
export const analyticsEvents = {
	appStart: (events) => trackEvent('app_start', events), // Changed to match your original event name
	startClicked: (events) => trackEvent('start_clicked', events), // Changed to match your original event name
	consentApproved: (events) => trackEvent('consent_approved', events),
	permissionsApproved: (events) => trackEvent('permissions_approved', events),
	userLoginSuccess: (events) => trackEvent('login', events),
	userLoginFailure: (events) => trackEvent('login_failed', events),
	surveyStarted: (events) => trackEvent('survey_started', events),
	surveyCompleted: (events) => trackEvent('survey_completed', events),
	trackTravelDay: (events) => trackEvent('travelday_approved', events),
	travelDayCompleted: (events) => trackEvent('travelday_completed', events),
	setUserProperties: (appStore, dataStore) => setAnalyticsUserProperties(appStore, dataStore),
}

export { analytics }
